import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { ResponseError } from '@backstage/errors';

export interface ActivityLogEvent {
  id: string;
  createdAt: string;
  eventType: string;
  actorRef: string;
  resourceRef?: string;
  projectRef?: string;
  sourcePlugin: string;
  status: 'success' | 'error';
  payload?: Record<string, unknown>;
}

export interface QueryEventsResponse {
  total: number;
  page: number;
  pageSize: number;
  items: ActivityLogEvent[];
}

export interface QueryEventsOptions {
  page?: number;
  pageSize?: number;
  from?: string;
  to?: string;
  eventType?: string;
  actorRef?: string;
  projectRef?: string;
  status?: string;
}

export interface AnalyticsDataPoint {
  label: string;
  count: number;
}

export interface AnalyticsSummaryResponse {
  groupBy: string;
  period: { from: string; to: string };
  data: AnalyticsDataPoint[];
}

export interface AnalyticsSummaryOptions {
  from?: string;
  to?: string;
  groupBy?: 'eventType' | 'day' | 'week' | 'month' | 'actor';
}

export interface ActivityLogClient {
  getEvents(options?: QueryEventsOptions): Promise<QueryEventsResponse>;
  getAnalyticsSummary(
    options?: AnalyticsSummaryOptions,
  ): Promise<AnalyticsSummaryResponse>;
}

export class ActivityLogClientImpl implements ActivityLogClient {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  private async getBaseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('activity-log');
  }

  private buildQuery(params: Record<string, unknown>): string {
    const entries = Object.entries(params).filter(
      ([, v]) => v !== undefined && v !== null && v !== '',
    );
    if (entries.length === 0) return '';
    return `?${new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString()}`;
  }

  async getEvents(options: QueryEventsOptions = {}): Promise<QueryEventsResponse> {
    const base = await this.getBaseUrl();
    const query = this.buildQuery({
      page: options.page ?? 1,
      pageSize: options.pageSize ?? 20,
      from: options.from,
      to: options.to,
      eventType: options.eventType,
      actorRef: options.actorRef,
      projectRef: options.projectRef,
      status: options.status,
    });
    const res = await this.fetchApi.fetch(`${base}/events${query}`);
    if (!res.ok) throw await ResponseError.fromResponse(res);
    return res.json();
  }

  async getAnalyticsSummary(
    options: AnalyticsSummaryOptions = {},
  ): Promise<AnalyticsSummaryResponse> {
    const base = await this.getBaseUrl();
    const query = this.buildQuery({
      from: options.from,
      to: options.to,
      groupBy: options.groupBy ?? 'eventType',
    });
    const res = await this.fetchApi.fetch(`${base}/analytics/summary${query}`);
    if (!res.ok) throw await ResponseError.fromResponse(res);
    const raw = await res.json();
    // Backend returns { buckets: [{key, count}] }; normalise to the frontend shape.
    const buckets: { key: string; count: number }[] = raw.buckets ?? raw.data ?? [];
    return {
      groupBy: options.groupBy ?? 'eventType',
      period: raw.period ?? { from: options.from ?? '', to: options.to ?? '' },
      data: buckets.map(b => ({ label: b.key ?? (b as any).label, count: Number(b.count) })),
    };
  }
}
